import { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  h1 {
    align-self: left;
    font-size: 30px;
    color: ${(props) => props.theme.colors.primaryText};
    margin-left: 30px;
    margin-bottom: 0px;
    font-weight: bold;

    /* Tablet responsive */
    @media (max-width: 768px) {
      font-size: 24px;
      margin-left: 20px;
      margin-bottom: 10px;
    }

    /* Mobile responsive */
    @media (max-width: 480px) {
      font-size: 20px;
      margin-left: 15px;
      margin-bottom: 8px;
    }
  }
`;

const SliderContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  width: 95%;
  margin: auto;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 20px 0;

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari */
  }

  /* Tablet responsive */
  @media (max-width: 768px) {
    gap: 15px;
    padding: 15px 0;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    gap: 8px;
    padding: 10px 0;
  }
`;

const Arrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.8);
  color: ${(props) => props.theme.colors.primaryText};
  border: none;
  cursor: pointer;
  z-index: 10;
  border-radius: 50%;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  ${(props) => (props.left ? 'left: 10px;' : 'right: 10px;')}
  margin: auto;
  opacity: 0;
  pointer-events: none;

  ${Container}:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    transform: translateY(-50%) scale(1.1);
    background: rgba(255, 255, 255, 1);
    color: black;
  }

  /* Tablet responsive */
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${(props) => (props.left ? 'left: 5px;' : 'right: 5px;')}
    opacity: 1;
    pointer-events: auto;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    ${(props) => (props.left ? 'left: 2px;' : 'right: 2px;')}
    opacity: 1;
    pointer-events: auto;
  }
`;

const DotsContainer = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 15px 0;
  margin-top: 5px;

  /* Mobile responsive */
  @media (max-width: 480px) {
    display: flex;
    gap: 6px;
    padding: 10px 0;
  }
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ccc;
  cursor: pointer;
  transition:
    background-color 0.3s ease,
    transform 0.3s ease;

  ${(props) =>
    props.active &&
    `
      background-color: #000;
      transform: scale(1.2);
    `}

  @media (max-width: 480px) {
    width: 6px;
    height: 6px;
  }
`;

// 슬라이더 컴포넌트
const SliderView = ({ title, children, style }) => {
  const sliderRef = useRef(null);
  const [slideWidth, setSlideWidth] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const updateSlideWidth = () => {
    if (!sliderRef.current) return;
    const firstSlide = sliderRef.current.children[0];
    if (firstSlide) {
      // Get gap size based on screen width (20px desktop, 15px tablet, 8px mobile)
      const isMobile = window.innerWidth <= 480;
      const isTablet = window.innerWidth <= 768;
      const gap = isMobile ? 8 : isTablet ? 15 : 20;
      setSlideWidth(firstSlide.clientWidth + gap); // 슬라이드 크기 + gap

      // Calculate total pages for dots
      if (isMobile && sliderRef.current.children.length > 0) {
        const itemsPerPage = 2;
        const totalItems = sliderRef.current.children.length;
        const pages = Math.ceil(totalItems / itemsPerPage);
        setTotalPages(pages);
      }
    }
  };

  const updateCurrentPage = () => {
    if (!sliderRef.current) return;
    const isMobile = window.innerWidth <= 480;
    if (isMobile) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const firstSlide = sliderRef.current.children[0];
      if (firstSlide) {
        const slideWidth = firstSlide.clientWidth + 8; // gap for mobile
        const itemsPerPage = 2;
        const currentPage = Math.round(
          scrollLeft / (slideWidth * itemsPerPage),
        );
        setCurrentPage(currentPage);
      }
    }
  };

  // Update slide width on mount and window resize
  useEffect(() => {
    updateSlideWidth();
    const handleResize = () => {
      updateSlideWidth();
      updateCurrentPage();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [children]);

  // Update current page on scroll
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      updateCurrentPage();
    };

    slider.addEventListener('scroll', handleScroll);
    return () => slider.removeEventListener('scroll', handleScroll);
  }, []);

  const handlePrev = () => {
    if (!sliderRef.current) return;

    const currentScroll = sliderRef.current.scrollLeft;
    // Use 2 items per move on mobile, 5 on desktop
    const isMobile = window.innerWidth <= 480;
    const itemsPerMove = isMobile ? 2 : 5;
    const scrollAmount = slideWidth * itemsPerMove;

    // Calculate how many items can actually be scrolled back
    const actualScrollAmount = Math.min(scrollAmount, currentScroll);

    sliderRef.current.scrollLeft -= actualScrollAmount;
    setTimeout(updateCurrentPage, 100);
  };

  const handleNext = () => {
    if (!sliderRef.current) return;

    const currentScroll = sliderRef.current.scrollLeft;
    const maxScroll =
      sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
    const remainingScroll = maxScroll - currentScroll;
    // Use 2 items per move on mobile, 5 on desktop
    const isMobile = window.innerWidth <= 480;
    const itemsPerMove = isMobile ? 2 : 5;
    const scrollAmount = slideWidth * itemsPerMove;

    // Calculate how much to scroll (either itemsPerMove items or remaining items if less)
    const actualScrollAmount = Math.min(scrollAmount, remainingScroll);

    sliderRef.current.scrollLeft += actualScrollAmount;
    setTimeout(updateCurrentPage, 100);
  };

  const goToPage = (pageIndex) => {
    if (!sliderRef.current) return;
    const isMobile = window.innerWidth <= 480;
    if (isMobile) {
      const firstSlide = sliderRef.current.children[0];
      if (firstSlide) {
        const slideWidth = firstSlide.clientWidth + 8; // gap for mobile
        const itemsPerPage = 2;
        const scrollPosition = slideWidth * itemsPerPage * pageIndex;
        sliderRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
        setCurrentPage(pageIndex);
      }
    }
  };

  return (
    <Container id={title} onMouseEnter={updateSlideWidth} style={style}>
      {title ? <h1>{title}</h1> : null}
      <SliderContainer>
        <Arrow left onClick={handlePrev}>
          &#10094;
        </Arrow>
        <ProductContainer id={title} ref={sliderRef}>
          {children}
        </ProductContainer>
        <Arrow onClick={handleNext}>&#10095;</Arrow>
      </SliderContainer>
      {totalPages > 1 && (
        <DotsContainer>
          {Array.from({ length: totalPages }).map((_, index) => (
            <Dot
              key={index}
              active={currentPage === index}
              onClick={() => goToPage(index)}
            />
          ))}
        </DotsContainer>
      )}
    </Container>
  );
};

export default SliderView;
